// Size options come out of Vendure in insertion order, which for ~120 of the
// catalog's option groups is reversed ("XL, L, M, S") or scrambled
// ("XXXL, S, M, L, XL, XXL"). Anything rendering a size list sorts it here
// rather than trusting the backend order.

const SIZE_ORDER = ['XXXS', 'XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', 'XXXXL']

// Numeric ranges (shoes/socks: "34-38", "41-46") sort after the letter sizes,
// ascending by their first number. Bare numbers are handled the same way.
const NUMERIC_RANGE = /^(\d+)/

// Whether a raw label/SKU segment is a recognised size rather than some other
// token (a color, a family name, …). Numeric ranges appear in SKUs with the
// hyphen dropped (`3438` = "34-38"), which is still purely digits, so the
// same numeric check used for sorting also works here.
export function isSizeToken(token: string): boolean {
  return SIZE_ORDER.includes(token.toUpperCase()) || /^\d+$/.test(token)
}

/**
 * Rank of a size label in display order. Unknown labels sort last, keeping
 * their relative order (the sort is stable), so an unrecognised token never
 * silently jumps to the front.
 */
export function sizeRank(label: string): number {
  const token = label.toUpperCase().trim()

  const known = SIZE_ORDER.indexOf(token)
  if (known !== -1)
    return known

  const numeric = token.match(NUMERIC_RANGE)
  if (numeric)
    return SIZE_ORDER.length + Number(numeric[1])

  return Number.MAX_SAFE_INTEGER
}

/** Sorts by size label, ascending (XXS → XXXXL). Returns a new array. */
export function sortBySize<T>(items: readonly T[], getLabel: (item: T) => string): T[] {
  return [...items].sort((a, b) => sizeRank(getLabel(a)) - sizeRank(getLabel(b)))
}

/**
 * Whether an option group is the size group.
 *
 * This backend's facets are empty, so the group is identified by its name in
 * whichever language it happened to be created in. Every surface that reads a
 * size has to agree on this — the PDP grid, quick add, and the order history
 * behind the size suggestion sit on the two halves of one equality check, so a
 * copy drifting here would not throw, it would just quietly stop matching.
 */
export function isSizeGroup(name?: string | null, code?: string | null): boolean {
  const lower = `${name ?? ''} ${code ?? ''}`.toLowerCase()
  return lower.includes('size') || lower.includes('velikost') || lower.includes('rozmiar')
}

/**
 * What a variant is called in a size list: its size, not its full name.
 *
 * The catalogue names every variant "<product name> - <size>", and the look
 * reads deliberately carry no option groups (one more join per variant on every
 * surface a look renders on, for a label the name already holds), so the size is
 * the name with the product's own name taken off the front. A variant named
 * differently keeps its full name rather than losing it.
 *
 * Shared because the outfit page both LABELS sizes with it and MATCHES sizes
 * across a colour swap with it — two products' variant names are the only thing
 * the storefront can compare a size by, so both sides have to derive it the
 * same way or a swap would silently drop the shopper's size.
 */
export function variantSizeLabel(productName: string, variantName: string): string {
  const name = variantName.trim()
  const product = productName.trim()
  if (product && name.toLowerCase().startsWith(product.toLowerCase())) {
    const rest = name.slice(product.length).replace(/^[\s\-–—:,/]+/, '').trim()
    if (rest)
      return rest
  }
  return name
}

/**
 * Categories whose size chart is the BOTTOMS one — waist and hips rather than
 * chest and underbust.
 */
const BOTTOMS_CATEGORIES = new Set(['shorts', 'leggings', 'joggers', 'pants', 'skirt'])

/**
 * Which hard-coded size chart a garment falls back to when nobody has authored
 * one for it in the Dashboard.
 *
 * Shared because two surfaces ask the same question about the same garment: the
 * product page for the product it is showing, and the outfit page for each
 * piece it tags. Two copies would eventually answer differently for one product
 * depending on which page the shopper opened the chart from.
 */
export function sizeGuideTypeForCategory(category?: string): 'tops' | 'bottoms' {
  return category && BOTTOMS_CATEGORIES.has(category) ? 'bottoms' : 'tops'
}

const SIZE_PILL_MIN_PX = 42
const SIZE_PILL_PADDING_PX = 8

/**
 * The narrowest a phone size pill may be so its label still fits, in px.
 *
 * The product page shares the row equally between its size pills
 * (`ProductInfo.vue`), so every pill is as wide as the widest label needs: a
 * shared minimum that is too small clips "XXXL" or a gift card's "1 000 Kč",
 * and one that is too large wraps a run that would have fitted. It is an
 * estimate rather than a measurement because it has to be in the server's HTML
 * — measuring after hydration would move the row.
 *
 * The glyph widths are the pill's 13px system font, measured in the browser
 * and rounded up: X 8.8px, L 7.3px, M 11.3px, digits ≈8px, hyphen 6.1px, a
 * space 3.6px. "XXXL" measures 33.6px; this gives 34.5px plus 8px of padding.
 * 42px is the floor, which lets XS–XXL share a row on a 320px phone.
 */
export function sizePillMinWidth(labels: readonly string[]): number {
  const widest = Math.max(0, ...labels.map(label => [...label].reduce((sum, char) => sum + glyphWidth(char), 0)))
  return Math.max(SIZE_PILL_MIN_PX, Math.ceil(widest + SIZE_PILL_PADDING_PX))
}

function glyphWidth(char: string): number {
  if (/\s/.test(char))
    return 3.6
  if (char === 'M' || char === 'W' || char === 'm' || char === 'w')
    return 12.5
  if ('1Iil/-.,'.includes(char))
    return 6.1
  if (char === 'L')
    return 7.5
  if (/[a-z]/.test(char) || /\p{Ll}/u.test(char))
    return 7.5
  return 9
}
